import { Box, FormControl, Grid } from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Context } from '../../service/Context';
import theme from '../../styles/theme';
import { capitalizar } from '../../utils/UtilesComun';
import { SelectWrapperApp } from '../custom/CustomComponents';

/**
 * Componente interno reutilizable para los selectores
 */
const BuscadorSelector = ({ id, name, value, options, onChange, onItemClick, renderOption, disabled }) => {
  const tieneValor = value !== undefined && value !== null && value !== '';
  const opcionesConSeleccion =
    tieneValor && Array.isArray(options) && !options.some((opt) => String(opt) === String(value))
      ? [value, ...options]
      : options || [];

  const handleBlurActive = () => {
    setTimeout(() => {
      if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }, 50);
  };

  return (
    <FormControl size="small">
      <SelectWrapperApp
        id={id}
        name={name}
        value={value ?? ''}
        onChange={(e) => {
          onChange(e.target.value);
          handleBlurActive();
        }}
        onClose={handleBlurActive}
        onItemClick={(val, e) => {
          if (onItemClick) {
            onItemClick(val, e);
          }
          handleBlurActive();
        }}
        displayEmpty
        options={opcionesConSeleccion}
        renderLabel={renderOption}
        sx={{
          width: '100%',
          backgroundColor: '#ffffff',
          borderRadius: `${theme.borderRadius}px`,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.08), 0 1px 2px -1px rgba(0, 0, 0, 0.04)',
          transition: 'all 0.15s ease-in-out',
          border: 'none',
          '& .MuiSelect-select': {
            py: 0.85,
            px: 1.5,
            fontSize: '0.86rem',
            fontWeight: 600,
            color: '#1e293b',
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiSelect-icon': {
            color: theme.palette.primary.main,
            fontSize: '1.2rem',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: `0.5px solid ${theme.palette.primary.main}`,
            transition: 'border-color 0.15s ease-in-out, border-width 0.15s ease-in-out',
          },
          '&:hover': {
            backgroundColor: '#ffffff',
            boxShadow: '0 2px 6px 0 rgba(15, 118, 110, 0.15)',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: '1px',
            },
          },
          '&.Mui-focused': {
            backgroundColor: '#ffffff',
            boxShadow: '0 0 0 2px rgba(15, 118, 110, 0.2)',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: '1.5px',
            },
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: `${theme.borderRadius}px`,
              border: `0.5px solid ${theme.palette.primary.main}`,
              boxShadow: '0 8px 24px -4px rgba(0, 0, 0, 0.12), 0 2px 6px -1px rgba(0, 0, 0, 0.04)',
              mt: 0.5,
              maxHeight: 320,
              '& .MuiMenuItem-root': {
                fontSize: '0.85rem',
                fontWeight: 500,
                py: 0.75,
                px: 1.5,
                borderRadius: '4px',
                mx: 0.5,
                my: 0.25,
                transition: 'all 0.15s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(15, 118, 110, 0.08)',
                  color: theme.palette.primary.main,
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(15, 118, 110, 0.12)',
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: 'rgba(15, 118, 110, 0.18)',
                  },
                },
              },
            },
          },
        }}
        disabled={disabled}
      />
    </FormControl>
  );
};

BuscadorSelector.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.any,
  options: PropTypes.array,
  onChange: PropTypes.func,
  onItemClick: PropTypes.func,
  renderOption: PropTypes.func,
  disabled: PropTypes.bool,
};

/**
 * Barra de búsqueda de ligas. Permite navegar fluidamente entre temporada, categoría, división y grupo.
 */
const BarraBuscarLiga = ({ datosLiga }) => {
  const {
    modificarTemporada,
    obtenerTemporadas,
    modificarCategoria,
    obtenerCategorias,
    modificarDivision,
    obtenerDivisiones,
    modificarGrupo,
    obtenerGrupos,
    obtenerIdLiga,
  } = useContext(Context);

  const navigate = useNavigate();

  // Opciones de cada selector
  const [temporadas, setTemporadas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [divisiones, setDivisiones] = useState([]);
  const [grupos, setGrupos] = useState([]);

  // Valores seleccionados
  const [temporadaSeleccionada, setTemporadaSeleccionada] = useState(datosLiga?.temporada || '');
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(datosLiga?.categoria || '');
  const [divisionSeleccionada, setDivisionSeleccionada] = useState(datosLiga?.division || '');
  const [grupoSeleccionado, setGrupoSeleccionado] = useState(datosLiga?.grupo || '');

  // Ref para mantener siempre actualizados los valores seleccionados de forma síncrona
  const seleccionRef = useRef({
    temporada: datosLiga?.temporada || '',
    categoria: datosLiga?.categoria || '',
    division: datosLiga?.division || '',
    grupo: datosLiga?.grupo || '',
  });

  // Estado de carga para evitar selecciones simultáneas en transiciones
  const [cargando, setCargando] = useState(false);
  const navegandoRef = useRef(false);

  // Sincronizar selectores y cargar opciones en paralelo cuando datosLiga cambia
  useEffect(() => {
    let cancelada = false;

    async function sincronizarConDatosLiga() {
      if (!datosLiga) {
        try {
          const temps = await obtenerTemporadas();
          if (!cancelada && temps) {
            setTemporadas(temps);
          }
        } catch (e) {
          console.error('Error al cargar temporadas iniciales:', e);
        }
        return;
      }

      const { temporada: temp, categoria: cat, division: div, grupo: grp } = datosLiga;

      setTemporadaSeleccionada(temp || '');
      setCategoriaSeleccionada(cat || '');
      setDivisionSeleccionada(div || '');
      setGrupoSeleccionado(grp || '');

      seleccionRef.current = {
        temporada: temp || '',
        categoria: cat || '',
        division: div || '',
        grupo: grp || '',
      };

      try {
        const [temps, cats, divs, grps] = await Promise.all([
          obtenerTemporadas(),
          temp ? obtenerCategorias(temp) : Promise.resolve([]),
          temp && cat ? obtenerDivisiones(temp, cat) : Promise.resolve([]),
          temp && cat && div ? obtenerGrupos(temp, cat, div) : Promise.resolve([]),
        ]);

        if (!cancelada) {
          if (Array.isArray(temps)) {
            setTemporadas(temps);
          }
          if (Array.isArray(cats)) {
            setCategorias(cats);
          }
          if (Array.isArray(divs)) {
            setDivisiones(divs);
          }
          if (Array.isArray(grps)) {
            setGrupos(grps);
          }
          navegandoRef.current = false;
        }
      } catch (err) {
        console.error('Error al inicializar opciones de BarraBuscarLiga:', err);
      }
    }

    sincronizarConDatosLiga();

    return () => {
      cancelada = true;
    };
  }, [datosLiga, obtenerTemporadas, obtenerCategorias, obtenerDivisiones, obtenerGrupos]);

  /* 1. CAMBIO DE TEMPORADA: Recalcula categorías, divisiones y grupos localmente sin refrescar la página */
  const handleChangeTemporada = useCallback(
    async (nuevaTemporada) => {
      if (!nuevaTemporada || nuevaTemporada === temporadaSeleccionada || navegandoRef.current) {
        return;
      }
      setCargando(true);
      try {
        seleccionRef.current.temporada = nuevaTemporada;
        setTemporadaSeleccionada(nuevaTemporada);
        await modificarTemporada(nuevaTemporada);

        const nuevasCategorias = await obtenerCategorias(nuevaTemporada);
        const cats = Array.isArray(nuevasCategorias) ? nuevasCategorias : [];
        setCategorias(cats);

        const catTarget = cats.includes(categoriaSeleccionada) ? categoriaSeleccionada : cats[0] || '';
        seleccionRef.current.categoria = catTarget;
        setCategoriaSeleccionada(catTarget);

        if (catTarget) {
          await modificarCategoria(catTarget);

          const nuevasDivisiones = await obtenerDivisiones(nuevaTemporada, catTarget);
          const divs = Array.isArray(nuevasDivisiones) ? nuevasDivisiones : [];
          setDivisiones(divs);

          const divTarget = divs.includes(divisionSeleccionada) ? divisionSeleccionada : divs[0] || '';
          seleccionRef.current.division = divTarget;
          setDivisionSeleccionada(divTarget);

          if (divTarget) {
            await modificarDivision(divTarget);

            const nuevosGrupos = await obtenerGrupos(nuevaTemporada, catTarget, divTarget);
            const grps = Array.isArray(nuevosGrupos) ? nuevosGrupos : [];
            setGrupos(grps);

            const grpTarget = grps.includes(grupoSeleccionado) ? grupoSeleccionado : grps[0] || '';
            seleccionRef.current.grupo = grpTarget;
            setGrupoSeleccionado(grpTarget);
          } else {
            seleccionRef.current.division = '';
            seleccionRef.current.grupo = '';
            setDivisionSeleccionada('');
            setGrupos([]);
            setGrupoSeleccionado('');
          }
        } else {
          seleccionRef.current.categoria = '';
          seleccionRef.current.division = '';
          seleccionRef.current.grupo = '';
          setCategoriaSeleccionada('');
          setDivisiones([]);
          setDivisionSeleccionada('');
          setGrupos([]);
          setGrupoSeleccionado('');
        }
      } catch (e) {
        console.error('Error al cambiar temporada:', e);
      } finally {
        setCargando(false);
      }
    },
    [
      temporadaSeleccionada,
      categoriaSeleccionada,
      divisionSeleccionada,
      grupoSeleccionado,
      modificarTemporada,
      obtenerCategorias,
      modificarCategoria,
      obtenerDivisiones,
      modificarDivision,
      obtenerGrupos,
    ]
  );

  /* 2. CAMBIO DE CATEGORIA: Recalcula divisiones y grupos localmente sin refrescar la página */
  const handleChangeCategoria = useCallback(
    async (nuevaCategoria) => {
      if (!nuevaCategoria || nuevaCategoria === categoriaSeleccionada || navegandoRef.current) {
        return;
      }
      setCargando(true);
      try {
        seleccionRef.current.categoria = nuevaCategoria;
        setCategoriaSeleccionada(nuevaCategoria);
        await modificarCategoria(nuevaCategoria);

        const tempActual = seleccionRef.current.temporada || temporadaSeleccionada;
        const nuevasDivisiones = await obtenerDivisiones(tempActual, nuevaCategoria);
        const divs = Array.isArray(nuevasDivisiones) ? nuevasDivisiones : [];
        setDivisiones(divs);

        const divTarget = divs.includes(divisionSeleccionada) ? divisionSeleccionada : divs[0] || '';
        seleccionRef.current.division = divTarget;
        setDivisionSeleccionada(divTarget);

        if (divTarget) {
          await modificarDivision(divTarget);

          const nuevosGrupos = await obtenerGrupos(tempActual, nuevaCategoria, divTarget);
          const grps = Array.isArray(nuevosGrupos) ? nuevosGrupos : [];
          setGrupos(grps);

          const grpTarget = grps.includes(grupoSeleccionado) ? grupoSeleccionado : grps[0] || '';
          seleccionRef.current.grupo = grpTarget;
          setGrupoSeleccionado(grpTarget);
        } else {
          seleccionRef.current.division = '';
          seleccionRef.current.grupo = '';
          setDivisionSeleccionada('');
          setGrupos([]);
          setGrupoSeleccionado('');
        }
      } catch (e) {
        console.error('Error al cambiar categoría:', e);
      } finally {
        setCargando(false);
      }
    },
    [
      categoriaSeleccionada,
      temporadaSeleccionada,
      divisionSeleccionada,
      grupoSeleccionado,
      modificarCategoria,
      obtenerDivisiones,
      modificarDivision,
      obtenerGrupos,
    ]
  );

  /* 3. CAMBIO DE DIVISION: Recalcula grupos localmente sin refrescar la página */
  const handleChangeDivision = useCallback(
    async (nuevaDivision) => {
      if (!nuevaDivision || nuevaDivision === divisionSeleccionada || navegandoRef.current) {
        return;
      }
      setCargando(true);
      try {
        seleccionRef.current.division = nuevaDivision;
        setDivisionSeleccionada(nuevaDivision);
        await modificarDivision(nuevaDivision);

        const tempActual = seleccionRef.current.temporada || temporadaSeleccionada;
        const catActual = seleccionRef.current.categoria || categoriaSeleccionada;
        const nuevosGrupos = await obtenerGrupos(tempActual, catActual, nuevaDivision);
        const grps = Array.isArray(nuevosGrupos) ? nuevosGrupos : [];
        setGrupos(grps);

        const grpTarget = grps.includes(grupoSeleccionado) ? grupoSeleccionado : grps[0] || '';
        seleccionRef.current.grupo = grpTarget;
        setGrupoSeleccionado(grpTarget);
      } catch (e) {
        console.error('Error al cambiar división:', e);
      } finally {
        setCargando(false);
      }
    },
    [
      divisionSeleccionada,
      temporadaSeleccionada,
      categoriaSeleccionada,
      grupoSeleccionado,
      modificarDivision,
      obtenerGrupos,
    ]
  );

  /* 4. CAMBIO/SELECCION DE GRUPO: Resuelve el ID y navega a la nueva liga */
  const executingGrupoRef = useRef(null);

  const handleSeleccionarGrupo = useCallback(
    async (nuevoGrupo) => {
      if (!nuevoGrupo || navegandoRef.current) {
        return;
      }
      if (executingGrupoRef.current === nuevoGrupo) {
        return;
      }
      executingGrupoRef.current = nuevoGrupo;
      setCargando(true);
      try {
        seleccionRef.current.grupo = nuevoGrupo;
        setGrupoSeleccionado(nuevoGrupo);
        await modificarGrupo(nuevoGrupo);

        const tempActual = seleccionRef.current.temporada || temporadaSeleccionada;
        const catActual = seleccionRef.current.categoria || categoriaSeleccionada;
        const divActual = seleccionRef.current.division || divisionSeleccionada;

        const idLiga = await obtenerIdLiga(tempActual, catActual, divActual, nuevoGrupo);
        if (idLiga && String(idLiga) !== String(datosLiga?.idLiga)) {
          navegandoRef.current = true;
          navigate(`/liga/${idLiga}`);
        }
      } catch (e) {
        console.error('Error al cambiar grupo:', e);
      } finally {
        setCargando(false);
        setTimeout(() => {
          executingGrupoRef.current = null;
        }, 300);
      }
    },
    [
      temporadaSeleccionada,
      categoriaSeleccionada,
      divisionSeleccionada,
      datosLiga?.idLiga,
      modificarGrupo,
      obtenerIdLiga,
      navigate,
    ]
  );

  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 0.5 }}>
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }} alignItems="center" justifyContent="center">
        {/* Temporada */}
        <Grid item>
          <BuscadorSelector
            id="temporada-select"
            name="temporada"
            value={temporadaSeleccionada}
            options={temporadas}
            onChange={handleChangeTemporada}
            disabled={cargando}
          />
        </Grid>

        {/* Categoria */}
        <Grid item>
          <BuscadorSelector
            id="categoria-select"
            name="categoria"
            value={categoriaSeleccionada}
            options={categorias}
            onChange={handleChangeCategoria}
            renderOption={(opt) => capitalizar(opt)}
            disabled={cargando}
          />
        </Grid>

        {/* Division */}
        <Grid item>
          <BuscadorSelector
            id="division-select"
            name="division"
            value={divisionSeleccionada}
            options={divisiones}
            onChange={handleChangeDivision}
            renderOption={(opt) => capitalizar(opt)}
            disabled={cargando}
          />
        </Grid>

        {/* Grupo */}
        <Grid item>
          <BuscadorSelector
            id="grupo-select"
            name="grupo"
            value={grupoSeleccionado}
            options={grupos}
            onChange={handleSeleccionarGrupo}
            onItemClick={handleSeleccionarGrupo}
            renderOption={(opt) => capitalizar(opt)}
            disabled={cargando}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

BarraBuscarLiga.propTypes = {
  datosLiga: PropTypes.shape({
    idLiga: PropTypes.number,
    temporada: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    categoria: PropTypes.string,
    division: PropTypes.string,
    grupo: PropTypes.string,
  }),
};

export default BarraBuscarLiga;
